// Phase 12 slice 1 — Turf Advisor chat page.
//
// Evergreen per-course thread: scrollback on the left, composer at the
// bottom. Pinecone RAG integration lands in slice 2 — until then the
// Advisor gets course + zones + 48h data + active memories from the
// briefing context builder.

import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { ApiError, api } from '../api/client.js';

interface ChatMessage {
  id: number;
  courseId: number;
  userId: number | null;
  role: 'user' | 'assistant';
  content: string;
  model: string | null;
  createdAt: string;
}

interface MessagesResponse {
  messages: ChatMessage[];
  anthropicConfigured: boolean;
  ragEnabled: boolean;
}

const STARTER_PROMPTS = [
  'Summarize the last 48 hours of conditions for me.',
  'Any zones trending down on NDRE this week?',
  'Which disease has the highest probability today — and why?',
  'Recommend a spray window for tomorrow based on the forecast.',
];

export function AdvisorPage() {
  const { courseSlug } = useParams();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [anthropicConfigured, setAnthropicConfigured] = useState(true);
  const [ragEnabled, setRagEnabled] = useState(false);
  const scrollRef = useRef<HTMLDivElement | null>(null);

  async function load() {
    if (!courseSlug) return;
    try {
      const r = await api.get<MessagesResponse>(`/courses/${courseSlug}/advisor/messages?limit=200`);
      setMessages(r.messages);
      setAnthropicConfigured(r.anthropicConfigured);
      setRagEnabled(r.ragEnabled);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    }
  }

  useEffect(() => {
    void load();
  }, [courseSlug]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages.length]);

  async function send(content: string) {
    if (!courseSlug) return;
    const text = content.trim();
    if (!text) return;
    setSending(true);
    setError(null);
    try {
      // Optimistically show the user's turn.
      const optimistic: ChatMessage = {
        id: -Date.now(),
        courseId: 0,
        userId: null,
        role: 'user',
        content: text,
        model: null,
        createdAt: new Date().toISOString(),
      };
      setMessages((prev) => [...prev, optimistic]);
      setDraft('');
      await api.post<{ assistantContent: string }>(`/courses/${courseSlug}/advisor/send`, { message: text });
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
      await load(); // re-sync if the optimistic row doesn't match
    } finally {
      setSending(false);
    }
  }

  if (!courseSlug) return null;

  return (
    <div className="flex flex-col h-[calc(100vh-4rem)]">
      <header className="px-6 md:px-10 py-4 border-b border-border bg-bg-card">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <p className="uppercase tracking-wider text-xs text-greeniq-400 font-semibold">Phase 12</p>
            <h1 className="mt-0.5 text-2xl md:text-3xl font-display font-bold text-fg">Turf Advisor</h1>
            <p className="text-xs text-fg-muted mt-0.5">
              Evergreen thread · Claude Sonnet 4.6 · {ragEnabled ? 'RAG enabled' : 'direct-context only (Pinecone not configured)'}
            </p>
          </div>
          {!anthropicConfigured && (
            <span className="text-xs px-2 py-1 rounded-md bg-error-muted text-[color:var(--color-error)] border border-[color:var(--color-error)]/30">
              ANTHROPIC_API_KEY not set — replies disabled
            </span>
          )}
        </div>
      </header>

      <div ref={scrollRef} className="flex-1 overflow-y-auto bg-bg-page px-6 md:px-10 py-6">
        {messages.length === 0 && (
          <div className="max-w-2xl mx-auto mt-8 text-center">
            <p className="text-fg-muted">
              Ask anything about the course — satellite trends, weather, disease risk, irrigation plans, what to spray tomorrow.
            </p>
            <div className="mt-6 grid gap-2">
              {STARTER_PROMPTS.map((p) => (
                <button
                  key={p}
                  onClick={() => void send(p)}
                  disabled={sending}
                  className="p-3 text-left text-sm bg-bg-card border border-border rounded-lg hover:border-greeniq-500/40 hover:shadow-sm"
                >
                  {p}
                </button>
              ))}
            </div>
          </div>
        )}

        <ol className="max-w-3xl mx-auto space-y-4">
          {messages.map((m) => (
            <li key={m.id} className={m.role === 'user' ? 'flex justify-end' : 'flex justify-start'}>
              <div
                className={[
                  'max-w-[85%] md:max-w-[70%] rounded-lg px-4 py-3 shadow-sm whitespace-pre-wrap',
                  m.role === 'user'
                    ? 'bg-greeniq-500 text-[#0b1014]'
                    : 'bg-bg-card border border-border text-fg',
                ].join(' ')}
              >
                {m.content}
              </div>
            </li>
          ))}
          {sending && (
            <li className="flex justify-start">
              <div className="rounded-lg px-4 py-3 bg-bg-card border border-border text-fg-muted text-sm italic">
                Thinking…
              </div>
            </li>
          )}
        </ol>
      </div>

      {error && (
        <div className="px-6 md:px-10 py-2 bg-error-muted border-t border-[color:var(--color-error)]/30 text-xs text-[color:var(--color-error)]">{error}</div>
      )}

      <div className="border-t border-border bg-bg-card px-4 md:px-6 py-3">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void send(draft);
          }}
          className="max-w-3xl mx-auto flex items-end gap-2"
        >
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                void send(draft);
              }
            }}
            placeholder="Ask about anything on the course…"
            rows={2}
            className="flex-1 border border-border bg-bg-input text-fg placeholder:text-fg-muted rounded-md py-2 px-3 resize-y text-sm focus:outline-none focus-visible:shadow-focus"
          />
          <button
            type="submit"
            disabled={sending || draft.trim().length === 0 || !anthropicConfigured}
            className="px-4 py-2.5 rounded-md bg-greeniq-500 text-[#0b1014] text-sm font-semibold hover:bg-greeniq-400 disabled:opacity-50"
          >
            {sending ? 'Sending…' : 'Send'}
          </button>
        </form>
      </div>
    </div>
  );
}
